import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { userDataService } from '../services/UserDataService';
import { getDirectVideoUrl } from '../utils/VideoUrlService';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import { useSeriesDetails } from '../hooks/useSeriesDetails';
import { useMovieDetails } from '../hooks/useMovieDetails';
import { useWatchlist } from '../hooks/useWatchlist';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { useGlobalMutedState } from '../hooks/useGlobalMutedState';
import GlobalPreviewPlayer from '../components/GlobalPreviewPlayer';
import { RelatedContentSkeleton, EpisodeCarouselSkeleton } from './common/SkeletonLoaders';
import spotlightService from '../services/SpotlightService';
import episodeSelectionService from '../services/EpisodeSelectionService';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './PDPModal.css';
import ReactionButtons from './ReactionButtons';
import { formatDuration, formatProgressTime, formatEpisodeDuration } from '../utils/durationUtils';
import metaTagManager from '../utils/MetaTagManager';
import SocialSharing from './SocialSharing';

const PDPModal = ({ channel, isVisible, onClose }) => {
  const { loginWithRedirect } = useAuth0();
  const [playButtonVideoId, setPlayButtonVideoId] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isAddingToWatchlist, setIsAddingToWatchlist] = useState(false);

  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Watch progress state for continue watching feature
  const [watchProgress, setWatchProgress] = useState(null);

  // Subscription/Paywall state
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  
  // Product data for purchase pricing (ad-gate)
  const [productData, setProductData] = useState(null);
  const [hasPaymentProfile, setHasPaymentProfile] = useState(false);
  const [showQuickPurchaseModal, setShowQuickPurchaseModal] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  // Add a refresh trigger to force re-fetch progress data
  const [progressRefreshTrigger, setProgressRefreshTrigger] = useState(0);

  // Get enhanced continue watching data to extract season information for dropdown
  const [enhancedContinueWatchingData, setEnhancedContinueWatchingData] = useState([]);
  const [enhancedDataLoading, setEnhancedDataLoading] = useState(false);

  const modalRef = useRef(null);
  const previewTimerRef = useRef(null);
  const seasonDropdownRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useStreamAuth();
  const { isMuted, toggleMuted } = useGlobalMutedState();
  const { addToWatchlist, removeFromWatchlist, isInWatchlist: checkIsInWatchlist } = useWatchlist();

  // Preview timing constants
  const PREVIEW_DELAY = 1000; // Delay before starting preview (ms)
  const PREVIEW_DURATION = 60000; // Maximum preview duration (60 seconds)

  // Content type detection - same logic as ChannelCard
  // Also check for childchannels as a fallback to detect series
  const { isMovie, isSeries } = useMemo(() => {
    const movie =
      channel?.programming_type === 'Movie' ||
      channel?.type === 'movie' ||
      channel?.content_type === 'movie';

    const series =
      channel?.programming_type === 'Series' ||
      channel?.type === 'series' ||
      channel?.content_type === 'series' ||
      // Fallback: if channel has childchannels with playlists, it's likely a series
      (channel?.childchannels && 
       Array.isArray(channel.childchannels) && 
       channel.childchannels.length > 0 &&
       channel.childchannels.some(child => 
         child.playlist && 
         Array.isArray(child.playlist) && 
         child.playlist.length > 0
       ));

    return { isMovie: movie, isSeries: series };
  }, [channel?.programming_type, channel?.type, channel?.content_type, channel?.childchannels]);

  // Always call both hooks but pass null when not needed (React hooks rule)
  const seriesData = useSeriesDetails(isSeries ? channel?.slug || channel?._id : null);
  const movieData = useMovieDetails(isMovie ? channel?.slug || channel?._id : null);

  // Extract data based on content type
  const {
    series,
    childChannels = [],
    currentChildChannel,
    episodes = [],
    selectedEpisode,
    relatedContent = [],
    loading: seriesLoading,
    error: seriesError,
    selectChildChannel,
    selectEpisode,
  } = seriesData || {};

  const {
    movie,
    relatedContent: movieRelatedContent = [],
    loading: movieLoading,
    error: movieError,
  } = movieData || {};

  // Determine current loading and error states
  const loading = isSeries ? seriesLoading : isMovie ? movieLoading : false;
  const error = isSeries ? seriesError : isMovie ? movieError : null;
  const currentRelatedContent = isSeries ? relatedContent : movieRelatedContent;

  // Extract channel data with movie/series specific handling
  const currentData = isSeries ? series : isMovie ? movie : channel;
  const title = currentData?.title || channel?.title || 'Untitled';
  const description =
    currentData?.description || channel?.description || 'No description available';
  const rating =
    currentData?.rating ||
    currentData?.content_rating ||
    channel?.rating ||
    channel?.content_rating ||
    '';
  const year = currentData?.year || currentData?.video?.year || channel?.year || '';

  // Movie-specific duration formatting
  const duration =
    isMovie && movie?.video?.duration
      ? (() => {
          const initialDuration = Math.ceil(Number(movie.video.duration));
          return initialDuration ? formatDuration(initialDuration) : 'Unknown duration';
        })()
      : currentData?.duration || channel?.duration || '';

  const genres = currentData?.genres || channel?.genres || [];

  // Movie-specific cast and crew data
  const actors = isMovie ? movie?.actors || movie?.video?.actors || [] : [];
  const directors = isMovie ? movie?.directors || movie?.video?.directors || [] : [];
  const writers = isMovie ? movie?.writers || movie?.video?.writers || [] : [];

  // Get wallpaper URL with fallbacks - optimized with width parameter for faster loading
  const getWallpaperUrl = () => {
    let rawImageUrl = null;
    
    if (channel?.wallpaper) {
      rawImageUrl = channel.wallpaper;
    } else if (channel?.image?.wallpaper) {
      rawImageUrl = channel.image.wallpaper;
    } else if (channel?.spotlight_poster) {
      rawImageUrl = channel.spotlight_poster;
    } else if (channel?.poster) {
      rawImageUrl = channel.poster;
    } else if (channel?.image?.poster) {
      rawImageUrl = channel.image.poster;
    }
    
    if (!rawImageUrl) return null;
    
    // Optimize wallpaper image - use viewport-appropriate size (max 1920px width for full HD)
    // This ensures fast loading while maintaining quality
    const viewportWidth = window.innerWidth;
    const optimizedWidth = Math.min(1920, Math.max(viewportWidth, 1280)); // Between 1280-1920px
    const optimizedHeight = Math.round(optimizedWidth * (9 / 16)); // 16:9 aspect ratio
    
    return optimizeImageUrl(rawImageUrl, {
      width: optimizedWidth,
      height: optimizedHeight,
      webp: true
    });
  };

  // Get logo URL with fallbacks
  const getLogoUrl = () => {
    if (channel?.logo) {
      return channel.logo;
    }
    if (channel?.image?.logo) {
      return channel.image.logo;
    }
    if (channel?.channel_logo) {
      return channel.channel_logo;
    }
    // Return null if no logo found
    return null;
  };

  // Calculate start time for preview (resumption point or 20% fallback)
  // This mirrors the exact logic from ThumbnailHoverModal
  const startTime = useMemo(() => {
    // For continue watching items, prioritize the resumption point from channel.resumption_data
    if (channel?.resumption_data?.point) {
      return channel.resumption_data.point;
    }

    // If we have resumption data from watchProgress, use that start time
    if (watchProgress && watchProgress.point) {
      return watchProgress.point;
    }

    // Calculate 20% fallback from available duration data
    let duration = 0;

    if (isMovie) {
      // For movies, try multiple duration sources
      if (movie?.video?.duration) {
        duration = Math.ceil(Number(movie.video.duration));
      } else if (movie?.duration) {
        duration = Math.ceil(Number(movie.duration));
      } else if (channel?.video?.duration) {
        duration = Math.ceil(Number(channel.video.duration));
      } else if (channel?.duration) {
        duration = Math.ceil(Number(channel.duration));
      }
    } else if (isSeries) {
      // For series, use the episode duration from watchProgress or current episode
      if (watchProgress && watchProgress.duration) {
        duration = Math.ceil(Number(watchProgress.duration));
      } else {
        const currentEpisode = currentChildChannel || selectedEpisode;
        if (currentEpisode?.video?.duration) {
          duration = Math.ceil(Number(currentEpisode.video.duration));
        }
      }
    }

    if (duration > 0) {
      const calculatedStartTime = Math.floor(duration * 0.3);
      return calculatedStartTime;
    }

    // No duration available, starting from beginning
    return 0;
  }, [
    channel?.resumption_data?.point,
    watchProgress?.point,
    watchProgress?.duration,
    isMovie,
    isSeries,
    movie?.video?.duration,
    movie?.duration,
    channel?.video?.duration,
    channel?.duration,
    channel?.childchannels,
    currentChildChannel?.video?.duration,
    currentChildChannel?.duration,
    selectedEpisode?.video?.duration,
    selectedEpisode?.duration,
  ]);

  // Determine video ID using the same logic as ThumbnailHoverModal
  // This ensures we get the video ID from the channel object directly (available immediately)
  const videoId = useMemo(() => {
    // If playButtonVideoId is explicitly set (from continue watching), use it
    if (playButtonVideoId && playButtonVideoId !== false) {
      return playButtonVideoId;
    }

    // For continue watching items, use channel.video_id or channel._id (same as ThumbnailHoverModal)
    if (channel?.resumption_data && (channel.video_id || channel._id)) {
      return channel.video_id || channel._id;
    }

    // For movies, check channel.video_id first (available immediately from channel call)
    // This matches ThumbnailHoverModal's logic which checks channel.video_id before fetching
    if (isMovie) {
      // Check channel.video_id first (this should be available from the channel API call)
      // Note: MoviesPage/SeriesPage data might not have video_id, so we check multiple sources
      if (channel?.video_id) {
        return channel.video_id;
      }
      
      // Then check channel.video._id
      if (channel?.video?._id) {
        return channel.video._id;
      }
      
      // Then check movie object (if loaded from useMovieDetails hook)
      if (movie) {
        if (movie.video_id) return movie.video_id;
        if (movie.video?._id) return movie.video._id;
      }
      
      // For MoviesPage/SeriesPage: channel data might come from getMoviesHomePage/getSeriesHomePage
      // which may not include video_id. In this case, channel._id might be the video ID
      // This is a fallback for when the channel object doesn't have nested video data
      if (channel?._id) return channel._id;
    }

    // For series, use selected episode or first episode
    if (isSeries) {
      // Try selected episode first
      if (selectedEpisode?._id) {
        return selectedEpisode._id;
      }
      
      // Try first episode from episodes array
      if (episodes.length > 0 && episodes[0]._id) {
        return episodes[0]._id;
      }
      
      // Try first episode from current season's playlist
      if (currentChildChannel?.playlist?.length > 0 && currentChildChannel.playlist[0]._id) {
        return currentChildChannel.playlist[0]._id;
      }
      
      // Try first episode from first season's playlist
      if (childChannels.length > 0 && childChannels[0].playlist?.length > 0 && childChannels[0].playlist[0]._id) {
        return childChannels[0].playlist[0]._id;
      }
    }

    // For single channels, check channel.video_id first (same as ThumbnailHoverModal)
    if (channel?.video_id) {
      return channel.video_id;
    }
    
    // Then check channel.video._id
    if (channel?.video?._id) {
      return channel.video._id;
    }
    
    // Last resort: use channel._id
    return channel?._id;
  }, [
    playButtonVideoId,
    channel?.resumption_data,
    channel?.video_id,
    channel?.video?._id,
    channel?._id,
    isMovie,
    isSeries,
    movie?.video_id,
    movie?.video?._id,
    selectedEpisode?._id,
    episodes,
    currentChildChannel?.playlist,
    childChannels,
  ]);

  // Setup preview with timer - using videoId (matching ThumbnailHoverModal)
  const setupPreview = useCallback(async () => {
    if (!channel) return;

    // Debug logging for video ID detection (always log for troubleshooting)
    if (!videoId) {
      console.warn('PDPModal: No video ID found for preview. Channel data:', {
        channelId: channel._id,
        channelTitle: channel.title,
        video_id: channel.video_id,
        'video._id': channel.video?._id,
        video: channel.video,
        isMovie,
        isSeries,
        movieLoading,
        movieVideoId: movie?.video_id || movie?.video?._id,
        playButtonVideoId,
        movieObject: movie ? 'loaded' : 'not loaded',
      });
    }

    const url = getDirectVideoUrl(videoId);
    if (url) {
      setPreviewUrl(url);

      // Set up preview timer
      previewTimerRef.current = setTimeout(() => {
        setShowVideo(false);
        setPreviewUrl(null);
      }, PREVIEW_DURATION);
    } else if (videoId) {
      console.warn('PDPModal: Could not generate preview URL for video ID:', videoId);
    }
  }, [channel, videoId, PREVIEW_DURATION, isMovie, isSeries, movie, movieLoading, playButtonVideoId]);

  // Handle modal visibility, URL query string, and meta tags
  useEffect(() => {
    if (isVisible && channel) {
      // Add channel slug to URL when modal opens
      const channelSlug = channel.slug || channel._id || 'unknown';
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('channel', channelSlug);
      window.history.pushState({}, '', currentUrl.toString());

      // Update meta tags for social sharing
      metaTagManager.updateChannelMetaTags(channel);
      
      // Debug meta tags in development
      if (process.env.NODE_ENV === 'development') {
        setTimeout(() => metaTagManager.debugSocialTags(), 100);
      }
    } else if (!isVisible) {
      // Remove channel query param when modal closes
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.delete('channel');
      window.history.pushState({}, '', currentUrl.toString());

      // Restore original meta tags when modal closes
      metaTagManager.restoreOriginalMetaTags();
    }
  }, [isVisible, channel]);

  // Cleanup meta tags on component unmount
  useEffect(() => {
    return () => {
      // Restore original meta tags when component unmounts
      if (isVisible) {
        metaTagManager.restoreOriginalMetaTags();
      }
    };
  }, [isVisible]);

  // Check subscription access when channel loads
  useEffect(() => {
    if (!isVisible || !channel?._id) return;

    // For appload channels, subscription_access will be included once backend is fixed
    if (channel.subscription_access) {
      console.log('💳 PDPModal: Using subscription_access from appload:', {
        channelId: channel._id,
        title: channel.title,
        unlocked: channel.subscription_access.unlocked,
        ads_enabled: channel.subscription_access.ads_enabled
      });
      setSubscriptionData(channel.subscription_access);
    } else {
      // For continue watching items or items without subscription_access,
      // we need to check the API to get proper subscription status
      const checkSubscriptionAccess = async () => {
        // First check if this channel has a paywall (is_product flag)
        const isProduct = channel.is_product || movie?.is_product || series?.is_product;
        
        if (!isProduct) {
          console.log('💳 PDPModal: No paywall (is_product: false), content is free:', {
            channelId: channel._id,
            title: channel.title
          });
          setSubscriptionData({ unlocked: true, ads_enabled: channel.ads_enabled !== false });
          return;
        }

        // Channel has paywall, check subscription access via API
        console.log('💳 PDPModal: Channel has paywall (is_product: true), checking access via API:', {
          channelId: channel._id,
          title: channel.title
        });

        setIsCheckingSubscription(true);
        try {
          const productId = channel.dspro_id || channel._id || movie?.dspro_id || movie?._id;
          const subscriptionAccess = await spotlightService.checkSubscriptionAccess(productId);
          setSubscriptionData(subscriptionAccess);
          
          console.log('💳 PDPModal: Subscription check complete:', {
            channelId: channel._id,
            title: channel.title,
            unlocked: subscriptionAccess?.unlocked,
            ads_enabled: subscriptionAccess?.ads_enabled,
            purchasable: subscriptionAccess?.purchasable
          });
        } catch (error) {
          console.error('💳 PDPModal: Failed to check subscription:', error);
          // Default to unlocked on error (allow playback with ads)
          setSubscriptionData({ unlocked: true, ads_enabled: true });
        } finally {
          setIsCheckingSubscription(false);
        }
      };

      checkSubscriptionAccess();
    }
  }, [isVisible, channel?._id, channel?.subscription_access, channel?.is_product, movie?.is_product, series?.is_product, movie?.dspro_id, movie?._id]);

  // Fetch product data and payment profile when content is purchasable
  useEffect(() => {
    if (!isVisible || !subscriptionData?.purchasable) return;
    
    const fetchProductAndPaymentData = async () => {
      // Get product ID from subscription_access or channel
      const productId = subscriptionData?.dspro_id || channel?.dspro_id || channel?._id;
      
      if (!productId) {
        console.warn('💳 PDPModal: No product ID available for purchasable content');
        return;
      }

      try {
        // Fetch product data for pricing
        console.log('💳 PDPModal: Fetching product data for:', productId);
        const productsResponse = await spotlightService.getProductsByChannel(productId);
        const products = productsResponse?.products || [];
        
        if (products.length > 0) {
          // Use the first product (or find the most appropriate one)
          setProductData(products[0]);
          console.log('💳 PDPModal: Product data loaded:', products[0]);
        }

        // Check if user has payment profile (for quick purchase flow)
        if (isAuthenticated) {
          const hasProfile = await spotlightService.hasPaymentProfile();
          setHasPaymentProfile(hasProfile);
          console.log('💳 PDPModal: Has payment profile:', hasProfile);
        }
      } catch (error) {
        console.error('💳 PDPModal: Error fetching product/payment data:', error);
      }
    };

    fetchProductAndPaymentData();
  }, [isVisible, subscriptionData?.purchasable, subscriptionData?.dspro_id, channel?.dspro_id, channel?._id, isAuthenticated]);

  // Start video when modal opens - matching ThumbnailHoverModal logic
  useEffect(() => {
    if (isVisible && channel) {
      // Start preview after delay
      const timer = setTimeout(async () => {
        // For movies, wait for watch progress data to be loaded (even if it's null)
        if (isMovie && watchProgress === undefined) {
          return;
        }

        // For movies from MoviesPage/SeriesPage: wait for movie data to load if we don't have video_id
        // This handles the case where channel data from getMoviesHomePage doesn't include video_id
        if (isMovie && !channel?.video_id && !channel?.video?._id && movieLoading) {
          console.log('PDPModal: Waiting for movie data to load (MoviesPage/SeriesPage data structure)');
          return;
        }

        // For series, wait for episodes to be loaded (but don't block if we have a video ID)
        if (isSeries && (!episodes || episodes.length === 0) && !videoId) {
          return;
        }

        // Only start preview if we have a valid video ID (same as ThumbnailHoverModal)
        if (!videoId) {
          console.warn('PDPModal: No video ID available for preview');
          return;
        }

        // Set up the preview URL before showing video
        await setupPreview();
        setShowVideo(true);
      }, PREVIEW_DELAY);

      return () => clearTimeout(timer);
    } else if (!isVisible) {
      // Reset video state when modal closes
      setShowVideo(false);
      setPreviewUrl(null);

      // Clean up preview timer
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    }
  }, [isVisible, channel, PREVIEW_DELAY, isMovie, isSeries, watchProgress, episodes, setupPreview, videoId, movieLoading]);

  // Cleanup on modal close
  useEffect(() => {
    if (!isVisible) {
      // Clean up preview
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }

      setShowVideo(false);
      setPreviewUrl(null);
      setVideoLoaded(false);
    }
  }, [isVisible]);

  // Use centralized duration formatting utility

  // Format series episode info Modern: "S1:E1 'Episode Title'"
  const formatSeriesEpisodeInfo = () => {
    const episodeData = lastWatchedEpisode;
    if (!episodeData || !isSeries) return null;

    // Extract season and episode numbers from the episode data
    // Use the same field names as the continue watching data structure
    let seasonNumber = episodeData.resumption_data?.seasonNumber || watchProgress.seasonNumber || 1;
    let episodeNumber =
      episodeData.resumption_data?.episodeNumber ||
      episodeData.episode ||
      episodeData.episode_number ||
      episodeData.episodeNumber ||
      1;

    // Handle case where channel.season might be a full title like "Flash Gordon - Season 1"
    if (episodeData.channel?.season && typeof episodeData.channel.season === 'string') {
      const seasonMatch = episodeData.channel.season.match(/Season\s+(\d+)/i);
      if (seasonMatch) {
        seasonNumber = parseInt(seasonMatch[1]) || 1;
      }
    }

    // If not available, try to parse from title or other fields
    if (!seasonNumber || !episodeNumber) {
      // Try to extract from title like "S1E1" or "Season 1 Episode 1"
      const titleMatch = episodeData.title?.match(/S(\d+)E(\d+)|Season\s+(\d+).*Episode\s+(\d+)/i);
      if (titleMatch) {
        seasonNumber = parseInt(titleMatch[1] || titleMatch[3]) || 1;
        episodeNumber = parseInt(titleMatch[2] || titleMatch[4]) || 1;
      }
    }

    // Clean episode title (remove season/episode prefixes if present)
    let cleanTitle = episodeData.title || 'Episode';
    cleanTitle = cleanTitle.replace(/^S\d+E\d+\s*[-:]?\s*/i, '');
    cleanTitle = cleanTitle.replace(/^Season\s+\d+.*Episode\s+\d+\s*[-:]?\s*/i, '');

    return `S${seasonNumber}:E${episodeNumber} "${cleanTitle}"`;
  };

  // Smart button logic based on subscription access and watch progress
  const getWatchButtonInfo = () => {
    const { unlocked, ads_enabled, purchasable } = subscriptionData || {};
    
    // Case 1: TVOD - Locked content (must purchase to watch)
    if (unlocked === false && purchasable) {
      // Note: productData.price already includes $ symbol from API
      const priceText = productData?.price ? ` - ${productData.price}` : '';
      return {
        text: `Buy Now${priceText}`,
        className: 'pdp-play-button pdp-buy-button',
        icon: 'lock',
        action: 'purchase',
      };
    }
    
    // Case 2: Unavailable - Locked and not purchasable
    if (unlocked === false && !purchasable) {
      return {
        text: 'Not Available',
        className: 'pdp-play-button pdp-unavailable-button',
        icon: 'lock',
        action: 'none',
        disabled: true,
      };
    }

    // Cases 3, 4, 5: Content is unlocked - show watch button based on progress
    // (Ad-gate secondary button is handled separately)
    if (watchProgress) {
      if (watchProgress.point === 0) {
        // Assume this is a Next Episode resumption point,
        // as this value should not be 0 otherwise
        return {
          text: 'Continue Watching',
          className: 'pdp-play-button pdp-play-button-chillFree resume',
          icon: 'resume',
          action: 'play',
        };
      } else if (watchProgress.progress < 1) {
        // We have progress on this asset but it's unfinished
        return {
          text: 'Resume',
          className: 'pdp-play-button pdp-play-button-chillFree resume',
          icon: 'resume',
          action: 'play',
        };
      } else if (watchProgress.progress === 1) {
        // We have finished this series, since this should only
        // appear if we have no other assets to watch
        return {
          text: 'Watch Again',
          className: 'pdp-play-button pdp-play-button-chillFree watch-again',
          icon: 'replay',
          action: 'play',
        };
      }
    }

    return {
      text: 'Watch Now',
      className: 'pdp-play-button pdp-play-button-chillFree',
      icon: 'play',
      action: 'play',
    };
  };

  const watchButtonInfo = getWatchButtonInfo();

  // Check if we should show the "Go Ad-Free" button (ad-gate scenario)
  const shouldShowGoAdFree = useMemo(() => {
    const { unlocked, ads_enabled, purchasable } = subscriptionData || {};
    return unlocked === true && ads_enabled === true && purchasable === true;
  }, [subscriptionData]);

  // Handle "Go Ad-Free" purchase flow
  const handleGoAdFree = useCallback(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }

    const productId = subscriptionData?.dspro_id || channel?.dspro_id || channel?._id;
    
    if (!productId) {
      console.error('PDPModal: No product ID available for purchase');
      return;
    }

    // If user has a payment profile, show quick purchase modal
    if (hasPaymentProfile) {
      setShowQuickPurchaseModal(true);
    } else {
      // Navigate to checkout page
      navigate(`/checkout/${productId}`, { 
        state: { channel, returnTo: window.location.pathname }
      });
    }
  }, [isAuthenticated, loginWithRedirect, subscriptionData, channel, hasPaymentProfile, navigate]);

  // Handle quick purchase confirmation (for users with saved payment method)
  const handleQuickPurchaseConfirm = async () => {
    if (!productData?.chargify_id) {
      setPurchaseError('Product configuration error. Please use the full checkout.');
      return;
    }

    setIsPurchasing(true);
    setPurchaseError(null);

    try {
      console.log('💳 PDPModal: Processing quick purchase for:', productData.chargify_id);
      
      // Subscribe using existing payment profile
      const result = await spotlightService.subscribeToProduct(productData.chargify_id);
      
      console.log('✅ PDPModal: Quick purchase successful:', result);
      console.log('🔍 PDPModal: Subscription response details:', JSON.stringify(result, null, 2));
      
      // CRITICAL: Refresh the client token after purchase to get updated entitlements
      // The old token doesn't know about the new purchase, so API calls would return stale data
      console.log('🔄 PDPModal: Refreshing client token after purchase...');
      try {
        const refreshedToken = await spotlightService.refreshClientToken();
        console.log('✅ PDPModal: Client token refreshed successfully, token length:', refreshedToken?.length);
      } catch (tokenError) {
        console.warn('⚠️ PDPModal: Could not refresh client token:', tokenError);
        // Continue anyway - will try to work with existing token
      }
      
      // Close the quick purchase modal
      setShowQuickPurchaseModal(false);
      
      // Clear ALL subscription caches to ensure fresh data
      spotlightService.clearSubscriptionCaches();
      
      // Small delay to ensure token is saved before navigation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate to watch page (now ad-free) with a page reload to ensure fresh data
      if (channel?.slug) {
        console.log('🚀 PDPModal: Navigating to watch page:', `/watch/${channel.slug}`);
        // Use window.location for full page reload with fresh token
        window.location.href = `/watch/${channel.slug}`;
      }
    } catch (error) {
      console.error('❌ PDPModal: Quick purchase failed:', error);
      setPurchaseError(error.message || 'Purchase failed. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  // Get episode progress from continue watching and watch again data - memoized to prevent excessive calls
  const getEpisodeProgress = useCallback(
    episode => {
      if (!isAuthenticated || !episode?._id) {
        return null;
      }

      // Get user's continue watching and watch again data from UserDataService
      const continueWatching = userDataService.getContinueWatching();
      const watchAgain = userDataService.getWatchAgain();

      // Combine both arrays to check for episode progress
      const allResumptions = [...(continueWatching || []), ...(watchAgain || [])];

      if (allResumptions.length === 0) {
        return null;
      }

      // Find progress for this specific episode
      const episodeProgress = allResumptions.find(item => {
        // Match by episode ID
        if (item._id === episode._id) {
          return true;
        }
        // Match by channel ID (for episodes)
        if (item.channel?._id === episode._id) {
          return true;
        }
        return false;
      });

      if (episodeProgress) {
        // Calculate progress if not already present
        let progress = episodeProgress.progress;
        if (!progress && episodeProgress.point && episodeProgress.duration) {
          progress = episodeProgress.point / episodeProgress.duration;
        }

        // Return progress data if we have any progress (including completed episodes)
        if (progress !== undefined && progress !== null) {
          // Determine if this episode should be considered completed
          // If it's in continue-watching, it's not completed (should be resumed)
          // If it's only in watch-again, it's completed
          const isInContinueWatching = continueWatching.some(item => item._id === episode._id);
          const isInWatchAgain = watchAgain.some(item => item._id === episode._id);

          let isCompleted = false;
          if (isInWatchAgain && !isInContinueWatching) {
            // Only in watch-again = completed
            isCompleted = true;
          } else if (isInContinueWatching) {
            // In continue-watching = not completed (should be resumed)
            isCompleted = false;
          } else {
            // Fallback: use progress percentage
            isCompleted = progress >= 0.95;
          }

          return {
            progress: progress, // Decimal (0.0 to 1.0)
            point: episodeProgress.point, // Current time in seconds
            duration: episodeProgress.duration, // Total duration in seconds
            updatedAt: episodeProgress.updatedAt, // When this episode was last watched
            isCompleted: isCompleted, // Mark as completed based on context
          };
        }
      }

      return null;
    },
    [isAuthenticated],
  );

  // Find the most recently watched episode to highlight it - memoized
  const getLastWatchedEpisode = useCallback(() => {
    if (!isAuthenticated || !channel?._id || !childChannels || childChannels.length === 0) {
      return null;
    }

    // Use enhanced continue watching data if available
    if (enhancedContinueWatchingData && enhancedContinueWatchingData.length > 0) {
      // Find the enhanced data item for this series
      const seriesEnhancedData = enhancedContinueWatchingData.find(item => {
        const itemId = item.parent_channel?._id || item.channel?._id || item._id;
        return itemId === channel._id;
      });

      if (seriesEnhancedData && seriesEnhancedData.episode) {
        return seriesEnhancedData;
      }
    }

    return null;
  }, [isAuthenticated, channel?._id, childChannels, enhancedContinueWatchingData]);

  // Fetch enhanced continue watching data when modal becomes visible
  useEffect(() => {
    const fetchEnhancedData = async () => {
      if (!isAuthenticated || !isVisible || !channel?._id) {
        setEnhancedContinueWatchingData([]);
        return;
      }

      setEnhancedDataLoading(true);
      try {
        const enhancedData = await userDataService.getEnhancedContinueWatchingData();
        setEnhancedContinueWatchingData(enhancedData);
      } catch (error) {
        console.error('PDPModal: Error fetching enhanced continue watching data:', error);
        setEnhancedContinueWatchingData([]);
      } finally {
        setEnhancedDataLoading(false);
      }
    };

    if (isVisible) {
      fetchEnhancedData();
    } else {
      setEnhancedContinueWatchingData([]);
    }
  }, [isVisible, isAuthenticated, channel?._id]);

  // Get the current season from enhanced continue watching data
  const getCurrentSeasonFromEnhancedData = useCallback(() => {
    if (!enhancedContinueWatchingData || enhancedContinueWatchingData.length === 0) {
      return null;
    }

    // Find the enhanced data item for this series
    const seriesEnhancedData = enhancedContinueWatchingData.find(item => {
      const itemId = item.parent_channel?._id || item.channel?._id || item._id;
      return itemId === channel._id;
    });

    if (!seriesEnhancedData) {
      return null;
    }

    // Extract season information from the enhanced data
    const seasonNumber =
      seriesEnhancedData.season || seriesEnhancedData.resumption_data?.seasonNumber || 1;

    // Find the corresponding season in childChannels
    const currentSeason = childChannels.find(season => {
      const seasonNum = season.season_number || season.season || 1;
      return seasonNum === seasonNumber;
    });

    return currentSeason || childChannels[0]; // Fallback to first season
  }, [enhancedContinueWatchingData, channel?._id, childChannels]);

  const lastWatchedEpisode = getLastWatchedEpisode();

  // Force refresh progress data when modal becomes visible (e.g., returning from watch page)
  useEffect(() => {
    if (isVisible && isAuthenticated && channel?._id) {
      // Add a small delay to ensure any new resumption data has been saved
      const refreshTimer = setTimeout(() => {
        setProgressRefreshTrigger(prev => prev + 1);
      }, 500); // 500ms delay to allow for API saves

      return () => clearTimeout(refreshTimer);
    }
  }, [isVisible, isAuthenticated, channel?._id]);

  // Show video once watch progress data is loaded for movies
  useEffect(() => {
    if (isVisible && isMovie && enhancedContinueWatchingData && !showVideo) {
      setShowVideo(true);
    }
  }, [isVisible, isMovie, enhancedContinueWatchingData, showVideo]);

  // Handle close modal
  const handleClose = () => {
    // Clean up preview timer if it exists
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    // Only clean up URL query string if we're not on a page that should preserve its URL structure
    // This prevents unwanted navigation on search pages, category pages, etc.
    const currentPath = window.location.pathname;
    const isContentSpecificPage = currentPath.startsWith('/watch/') || currentPath === '/';

    if (isContentSpecificPage) {
      // Only manipulate URL on content-specific pages where the modal affects the URL
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.delete('channel');
      window.history.pushState({}, '', currentUrl.toString());
    }
    // For other pages (search, categories, etc.), don't manipulate the URL

    setShowVideo(false);
    onClose();
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  // Handle errors gracefully
  if (error) {
    console.error('PDPModal data loading error:', error);
    return createPortal(
      <div className="pdp-modal-overlay" onClick={onClose}>
        <div className="pdp-modal-container" onClick={e => e.stopPropagation()}>
          <button className="pdp-modal-close" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="pdp-error-container">
            <h2>Unable to Load Content Details</h2>
            <p>
              We're having trouble loading the details for this content. Please try again later.
            </p>
            <button onClick={onClose} className="pdp-error-button">
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Handle watch now - with episode support for series
  const handleWatchNow = () => {
    // If content is locked, navigate to checkout
    if (subscriptionData?.unlocked === false) {
      const channelId = channel?.dspro_id || channel?._id;
      if (channelId) {
        navigate(`/checkout/${channelId}`, { state: { channel } });
        onClose(); // Close modal
      } else {
        console.warn('No channel ID available for checkout navigation');
      }
      return;
    }

    // Otherwise, navigate to watch page
    if (channel?.slug) {
      let watchUrl = `/watch/${channel.slug}`;

      // For series, if a specific episode is provided or selected, add it to URL
      if (isSeries && playButtonVideoId) {
        watchUrl += `?_id=${playButtonVideoId}`;
      }

      navigate(watchUrl);
    } else {
      console.warn('No channel slug available for watch navigation');
    }

    // Clean up modal state without URL manipulation since we're navigating
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    setShowVideo(false);
    onClose();
  };

  // Handle watch trailer - navigate to dedicated trailer page
  // Get trailer data from channel, movie, or series object
  const trailerData = useMemo(() => {
    // Check channel.trailer first
    if (channel?.trailer?.video_id) {
      return {
        videoId: channel.trailer.video_id,
        slug: channel.slug
      };
    }
    // Check movie.trailer (for movies opened from continue watching)
    if (movie?.trailer?.video_id) {
      return {
        videoId: movie.trailer.video_id,
        slug: movie.slug || channel?.slug
      };
    }
    // Check series.trailer (for series opened from continue watching)
    if (series?.trailer?.video_id) {
      return {
        videoId: series.trailer.video_id,
        slug: series.slug || channel?.slug
      };
    }
    return null;
  }, [channel?.trailer?.video_id, channel?.slug, movie?.trailer?.video_id, movie?.slug, series?.trailer?.video_id, series?.slug]);

  const handleWatchTrailer = () => {
    if (!trailerData) {
      console.warn('No trailer video ID available');
      return;
    }

    // Navigate to dedicated trailer page
    if (trailerData.slug) {
      const trailerUrl = `/trailer/${trailerData.slug}/${trailerData.videoId}`;
      navigate(trailerUrl);
    } else {
      console.warn('No channel slug available for trailer navigation');
    }

    // Clean up modal state
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    setShowVideo(false);
    onClose();
  };

  // Handle watchlist toggle
  // Generic watchlist toggle function that can work with any channel
  const handleWatchlistToggleForChannel = async targetChannel => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      loginWithRedirect();
      return;
    }

    // Determine the correct channel ID and parent channel ID
    let channelId = targetChannel?._id;
    let parentChannelId = null;

    // Check if this is a series
    const isSeries =
      targetChannel?.childchannels?.length > 0 ||
      targetChannel?.programming_type === 'Series' ||
      targetChannel?.type === 'series' ||
      targetChannel?.content_type === 'series';

    if (isSeries && targetChannel?.childchannels?.length > 0) {
      // For series, use the first child season
      channelId = targetChannel.childchannels[0]._id;
      parentChannelId = targetChannel._id;
    }

    if (!channelId) {
      console.error('PDPModal: No valid channel ID available for watchlist operation');
      return;
    }

    try {
      // For series, we need to check if any child is in the watchlist
      // For movies, check the item itself
      let isCurrentlyInWatchlist = false;

      if (isSeries && targetChannel?.childchannels?.length > 0) {
        // Check if any child season is in the watchlist
        isCurrentlyInWatchlist = targetChannel.childchannels.some(child =>
          userDataService.isInWatchlist(child._id, targetChannel._id),
        );
      } else {
        // For movies or series without childchannels, check the item itself
        isCurrentlyInWatchlist = userDataService.isInWatchlist(channelId, parentChannelId);
      }

      if (isCurrentlyInWatchlist) {
        // Remove the first child season from watchlist
        await removeFromWatchlist(channelId);
      } else {
        // Add the first child season to watchlist
        await addToWatchlist(channelId, parentChannelId);
      }
    } catch (error) {
      console.error('PDPModal: Error toggling watchlist:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  // Main watchlist toggle for the current channel
  const handleWatchlistToggle = async () => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      loginWithRedirect();
      return;
    }

    console.log('🔍 PDPModal: handleWatchlistToggle called');
    console.log('🔍 PDPModal: channel object:', channel);
    console.log('🔍 PDPModal: channel._id:', channel?._id);
    console.log('🔍 PDPModal: channel.contentId:', channel?.contentId);
    console.log('🔍 PDPModal: channel.dspro_id:', channel?.dspro_id);
    console.log('🔍 PDPModal: channel.resumption_data:', channel?.resumption_data);

    // For continue watching items, we need to use the content ID, not the resumption ID
    let channelId = channel?._id;
    let parentChannelId = null;

    // If this is a continue watching item, use the content ID for watchlist operations
    if (channel?.contentId) {
      // Use the content ID that was set in useContinueWatching
      console.log('🔍 PDPModal: Using contentId for watchlist:', channel.contentId);
      channelId = channel.contentId;
    } else if (channel?.resumption_data) {
      // Fallback: If this is a continue watching item (has resumption_data), use the appropriate channel ID
      console.log('🔍 PDPModal: Using resumption_data fallback');
      if (channel.resumption_data.parent_channel) {
        channelId = channel.resumption_data.parent_channel._id;
      } else if (channel.resumption_data.channel) {
        channelId = channel.resumption_data.channel._id;
      }
    } else if (channel?.childchannels && channel.childchannels.length > 0) {
      // This is a series from AppLoad with childchannels
      // Use the first child channel as the channelId and the parent as parentChannelId
      channelId = channel.childchannels[0]._id;
      parentChannelId = channel._id;
    }

    if (!channelId) {
      console.error('PDPModal: No valid channel ID available for watchlist operation');
      return;
    }

    setIsAddingToWatchlist(true);
    try {
      if (isInWatchlist) {
        const result = await userDataService.removeFromWatchlist(channelId);
        if (result.success) {
          setIsInWatchlist(false);
        } else {
          console.error('PDPModal: Failed to remove from watchlist:', result.error);
          // Show user-friendly error message
          alert('Failed to remove from My List. Please try again.');
        }
      } else {
        const result = await userDataService.addToWatchlist(channelId, parentChannelId);
        if (result.success) {
          setIsInWatchlist(true);
        } else {
          console.error('PDPModal: Failed to add to watchlist:', result.error);
          // Show user-friendly error message
          alert('Failed to add to My List. Please try again.');
        }
      }
    } catch (error) {
      console.error('PDPModal: Error toggling watchlist:', error);
      // Show user-friendly error message
      alert('Something went wrong. Please try again.');
    } finally {
      setIsAddingToWatchlist(false);
    }
  };

  // Handle volume toggle
  const handleVolumeToggle = () => {
    toggleMuted();
  };

  // Handle video ready event
  const handleVideoReady = () => {
    setVideoLoaded(true);
  };

  // Handle video error event
  const handleVideoError = error => {
    console.warn('PDPModal: Global preview player error:', error);
    setShowVideo(false);
    setPreviewUrl(null);
    setVideoLoaded(false);
  };

  // Handle video end event - fade to wallpaper
  const handleVideoEnd = () => {
    // Fade out video and show wallpaper background
    setShowVideo(false);
    setPreviewUrl(null);
  };

  // Check watchlist status
  useEffect(() => {
    const checkWatchlistStatus = async () => {
      if (isAuthenticated && channel?._id) {
        try {
          // For continue watching items, we need to use the content ID, not the resumption ID
          let channelId = channel._id;

          // If this is a continue watching item, use the content ID for watchlist operations
          if (channel?.contentId) {
            // Use the content ID that was set in useContinueWatching
            channelId = channel.contentId;
          } else if (channel?.resumption_data) {
            // Fallback: If this is a continue watching item (has resumption_data), use the appropriate channel ID
            if (channel.resumption_data.parent_channel) {
              channelId = channel.resumption_data.parent_channel._id;
            } else if (channel.resumption_data.channel) {
              channelId = channel.resumption_data.channel._id;
            }
          } else if (channel?.childchannels && channel.childchannels.length > 0) {
            // This is a series from AppLoad with childchannels
            // Use the first child channel as the channelId and the parent as parentChannelId
            channelId = channel.childchannels[0]._id;
            const parentChannelId = channel._id;
            const isInList = userDataService.isInWatchlist(channelId, parentChannelId);
            setIsInWatchlist(isInList);
            return;
          }

          const isInList = userDataService.isInWatchlist(channelId);
          setIsInWatchlist(isInList);
        } catch (error) {
          console.error('PDPModal: Error checking watchlist status:', error);
        }
      }
    };

    if (isVisible) {
      checkWatchlistStatus();
    }
  }, [
    isVisible,
    isAuthenticated,
    channel?._id,
    channel?.contentId,
    channel?.resumption_data,
    channel?.childchannels,
  ]);

  // Handle episode selection - navigate to watch page with specific episode
  const handleEpisodeSelect = useCallback(
    episode => {
      if (channel?.slug && episode?._id) {
        const watchUrl = `/watch/${channel.slug}?_id=${episode._id}`;
        navigate(watchUrl);

        // Clean up modal state without URL manipulation since we're navigating
        if (previewTimerRef.current) {
          clearTimeout(previewTimerRef.current);
          previewTimerRef.current = null;
        }
        setShowVideo(false);
        onClose();
      } else {
        console.warn('PDPModal: Missing channel slug or episode ID for navigation:', {
          channelSlug: channel?.slug,
          episodeId: episode?._id,
        });
      }
    },
    [channel?.slug, navigate],
  );

  // Handle season selection
  const handleSeasonSelect = childChannel => {
    if (selectChildChannel) {
      selectChildChannel(childChannel);
    }
    setShowSeasonDropdown(false);
  };

  // Auto-select the correct season when enhanced data is loaded
  useEffect(() => {
    if (enhancedDataLoading && enhancedContinueWatchingData.length) {
      const progress = enhancedContinueWatchingData.find(ewd => ewd._id === channel._id);
      if (progress?.resumption_data) {
        setWatchProgress(progress.resumption_data);
        setPlayButtonVideoId(progress.video_id);
      } else if (channel?.video_id) {
        setWatchProgress(null);
        setPlayButtonVideoId(channel.video_id);
      }
    } else if (channel?.video_id) {
      setWatchProgress(null);
      setPlayButtonVideoId(channel.video_id);
    }
    if (
      isSeries &&
      childChannels.length > 0 &&
      enhancedContinueWatchingData.length > 0 &&
      !enhancedDataLoading
    ) {
      const currentSeasonFromEnhancedData = getCurrentSeasonFromEnhancedData();

      if (currentSeasonFromEnhancedData && selectChildChannel) {
        // Only select if it's different from the current selection
        if (currentChildChannel?._id !== currentSeasonFromEnhancedData._id) {
          selectChildChannel(currentSeasonFromEnhancedData);
        }
      }
    }
  }, [
    isSeries,
    childChannels,
    enhancedContinueWatchingData,
    enhancedDataLoading,
    getCurrentSeasonFromEnhancedData,
    selectChildChannel,
    currentChildChannel?._id,
  ]);

  // Season dropdown component
  const SeasonDropdown = () => {
    // Get the current season from enhanced continue watching data
    const currentSeasonFromEnhancedData = getCurrentSeasonFromEnhancedData();

    // Determine which season to show as current
    // Priority: enhanced data season > currentChildChannel > first season
    const currentSeason = currentSeasonFromEnhancedData || currentChildChannel || childChannels[0];

    return (
      <div className="relative" ref={seasonDropdownRef}>
        <button
          className="flex items-center px-4 py-2 text-white bg-gray-700 rounded-lg hover:bg-gray-600"
          onClick={() => setShowSeasonDropdown(!showSeasonDropdown)}
        >
          <span>{currentSeason?.displayTitle || 'Season 1'}</span>
          <svg
            className={`h-5 w-5 ml-2 transition-transform ${
              showSeasonDropdown ? 'rotate-180' : ''
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {showSeasonDropdown && (
          <div className="absolute right-0 z-50 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl min-w-48">
            {childChannels.map(childChannel => (
              <button
                key={childChannel._id}
                onClick={() => handleSeasonSelect(childChannel)}
                className={`block w-full px-4 py-2 text-left text-white hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg ${
                  childChannel._id === currentSeason?._id ? 'bg-gray-600' : ''
                }`}
              >
                {childChannel.displayTitle}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Slider settings for episode carousel
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (!isVisible || !channel) return null;

  return createPortal(
    <div className="pdp-modal-overlay" onClick={handleClose}>
      <div className="pdp-modal-container" onClick={e => e.stopPropagation()} ref={modalRef}>
        {/* Close Button */}
        <button className="pdp-modal-close" onClick={handleClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Hero Section */}
        <div className="pdp-hero-section">
          <div className="pdp-hero-media">
            {showVideo && previewUrl ? (
              <GlobalPreviewPlayer
                hlsUrl={previewUrl}
                autoPlay={true}
                muted={isMuted}
                controls={false}
                qualityMode="pdp"
                className={`preview-player-pdp ${videoLoaded ? 'visible' : 'hidden'}`}
                startTime={startTime}
                onReady={handleVideoReady}
                onError={handleVideoError}
                onEnd={handleVideoEnd}
              />
            ) : (
              <div
                className="pdp-hero-image"
                style={{
                  width: '100%',
                  height: '500px',
                  background: `url(${getWallpaperUrl()}) center/cover`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            )}

            {/* Hero Gradient Overlay */}
            <div className="pdp-hero-gradient" />

            {/* Hero Content */}
            <div className="pdp-hero-content">
              {/* Title or Logo */}
              {(() => {
                const logoUrl = getLogoUrl();

                return logoUrl ? (
                  <div className="pdp-hero-logo-container">
                    <img
                      src={logoUrl}
                      alt={title}
                      className="pdp-hero-logo"
                      onError={e => {
                        // Fallback to text title if logo fails to load
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <h1 className="pdp-hero-title" style={{ display: 'none' }}>
                      {title}
                    </h1>
                  </div>
                ) : (
                  <h1 className="pdp-hero-title">{title}</h1>
                );
              })()}

              {/* Action Buttons */}
              <div className="pdp-hero-actions">
                <button className={watchButtonInfo.className} onClick={handleWatchNow}>
                  {watchButtonInfo.icon === 'lock' && (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {watchButtonInfo.icon === 'play' && (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                  {watchButtonInfo.icon === 'resume' && (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                      <circle cx="12" cy="12" r="2" fill="#56bdaf" />
                    </svg>
                  )}
                  {watchButtonInfo.icon === 'replay' && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="1 4 1 10 7 10" />
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                  )}
                  {watchButtonInfo.text}
                </button>

                {/* Go Ad-Free Button - only show for ad-gated content */}
                {shouldShowGoAdFree && (
                  <button className="pdp-go-adfree-button" onClick={handleGoAdFree}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </svg>
                    Go Ad-Free
                  </button>
                )}

                {/* Trailer Button - only show if trailer exists (check channel, movie, or series) */}
                {trailerData && (
                  <button className="pdp-trailer-button" onClick={handleWatchTrailer}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
                      <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
                    </svg>
                    Trailer
                  </button>
                )}

                <button
                  className={`pdp-add-button ${isInWatchlist ? 'in-watchlist' : ''}`}
                  onClick={handleWatchlistToggle}
                  disabled={isAddingToWatchlist}
                >
                  {isAddingToWatchlist ? (
                    <svg
                      className="animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 12a9 9 0 11-6.219-8.56" />
                    </svg>
                  ) : isInWatchlist ? (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <line x1="12" y1="6" x2="12" y2="18" />
                      <line x1="6" y1="12" x2="18" y2="12" />
                    </svg>
                  )}
                </button>

                {/* Reaction Buttons */}
                {(() => {
                  const reactionChannelId = channel?.dspro_id || channel?.contentId || channel?._id;
                  console.log('🔍 PDPModal: ReactionButtons channelId:', {
                    dspro_id: channel?.dspro_id,
                    contentId: channel?.contentId,
                    _id: channel?._id,
                    finalChannelId: reactionChannelId
                  });
                  return (
                    <ReactionButtons 
                      channelId={reactionChannelId}
                      key={channel?.dspro_id || channel?._id}
                      initialReaction={channel?.user_reaction || channel?.reaction || null}
                      size="large"
                      className="pdp-reaction-buttons"
                    />
                  );
                })()}

                {/* Volume Control */}
                <button className="pdp-volume-button-action" onClick={handleVolumeToggle}>
                  {isMuted ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Continue Watching Progress Bar - Modern Style */}
              {watchProgress && (
                <div className="pdp-progress-container">
                  <div className="pdp-progress-bar">
                    <div
                      className="pdp-progress-fill"
                      style={{ width: `${watchProgress.progress * 100}%` }}
                    />
                  </div>
                  {/* Modern progress text layout */}
                  <div className="pdp-progress-text-container">
                    {watchProgress.seasonNumber ? (
                      <>
                        <div className="pdp-progress-episode-info">
                          {formatSeriesEpisodeInfo(watchProgress)}
                        </div>
                        <div className="pdp-progress-time">
                          {formatProgressTime(watchProgress.point)} of{' '}
                          {formatProgressTime(watchProgress.duration)}
                        </div>
                      </>
                    ) : (
                      <div className="pdp-progress-text">
                        {formatProgressTime(watchProgress.point)} of{' '}
                        {formatProgressTime(watchProgress.duration)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="pdp-content-section">
          {/* Basic Metadata Row */}
          <div className="pdp-basic-metadata">
            {year && <span className="pdp-year">{year}</span>}
            {rating && <span className="pdp-rating">{rating}</span>}
            {duration && <span className="pdp-duration">{duration}</span>}
            {isSeries && childChannels.length > 1 && (
              <span className="pdp-seasons">
                {childChannels.length} Season{childChannels.length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Two Column Layout */}
          <div className="pdp-content-columns">
            {/* Left Column - Description */}
            <div className="pdp-content-left">
              <p className="pdp-description">{description}</p>
            </div>

            {/* Right Column - Metadata */}
            <div className="pdp-content-right">
              {/* Genres */}
              {genres.length > 0 && (
                <div className="pdp-metadata-item">
                  <span className="pdp-metadata-label">Genres: </span>
                  <span className="pdp-metadata-value">{genres.join(', ')}</span>
                </div>
              )}

              {/* Cast and Crew (for movies) */}
              {isMovie && actors.length > 0 && (
                <div className="pdp-metadata-item">
                  <span className="pdp-metadata-label">Cast: </span>
                  <span className="pdp-metadata-value">{actors.slice(0, 5).join(', ')}</span>
                </div>
              )}

              {isMovie && directors.length > 0 && (
                <div className="pdp-metadata-item">
                  <span className="pdp-metadata-label">Director: </span>
                  <span className="pdp-metadata-value">{directors.join(', ')}</span>
                </div>
              )}

              {isMovie && writers.length > 0 && (
                <div className="pdp-metadata-item">
                  <span className="pdp-metadata-label">Writers: </span>
                  <span className="pdp-metadata-value">{writers.slice(0, 3).join(', ')}</span>
                </div>
              )}

              {/* Social Sharing */}
              <SocialSharing 
                channelSlug={channel?.slug} 
                title={title}
                description={description}
                image={getWallpaperUrl() || optimizeImageUrl(
                  channel?.spotlight_poster || channel?.poster || channel?.thumb,
                  { width: 1200, height: 630, webp: false } // Social media optimal size
                )}
              />
            </div>
          </div>

          {/* Episodes Section (for series) - Netflix style layout */}
          {isSeries && childChannels.length > 0 && (
            <div className="pdp-episodes-section">
              <div className="pdp-episodes-header">
                <h3>Episodes</h3>
                <SeasonDropdown />
              </div>

              {loading ? (
                <EpisodeCarouselSkeleton slidesToShow={4} />
              ) : episodes.length > 0 ? (
                <div className="pdp-episodes-list">
                  {episodes.map((episode, index) => {
                    const episodeProgress = getEpisodeProgress(episode);
                    const isLastWatched =
                      lastWatchedEpisode && lastWatchedEpisode.video_id === episode._id;

                    return (
                      <div
                        key={episode._id}
                        className={`pdp-episode-item ${
                          isLastWatched ? 'pdp-episode-last-watched' : ''
                        }`}
                      >
                        <div className="pdp-episode-number">{index + 1}</div>
                        <div className="pdp-episode-thumbnail">
                          {episode.thumbnail ? (
                            <img
                              src={optimizeImageUrl(
                                episode.thumbnail,
                                getResponsiveImagePreset('EPISODE_THUMBNAIL'),
                              )}
                              alt={episode.title}
                              className="pdp-episode-image"
                            />
                          ) : (
                            <div className="pdp-episode-placeholder">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-8 h-8 text-white/30"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1 1v14a1 1 0 001 1z"
                                />
                              </svg>
                            </div>
                          )}

                          {/* Episode Progress Bar */}
                          {episodeProgress && (
                            <div className="pdp-episode-progress-bar">
                              <div
                                className={`pdp-episode-progress-fill ${
                                  episodeProgress.isCompleted ? 'completed' : ''
                                }`}
                                style={{ width: `${episodeProgress.progress * 100}%` }}
                              />
                            </div>
                          )}

                          <button
                            className={`pdp-episode-play-button ${
                              episodeProgress && episodeProgress.isCompleted ? 'completed' : ''
                            }`}
                            onClick={() => handleEpisodeSelect(episode)}
                          >
                            {episodeProgress && episodeProgress.isCompleted ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-6 h-6"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-6 h-6"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <div className="pdp-episode-info">
                          <div className="pdp-episode-header">
                            <h4 className="pdp-episode-title">{episode.title}</h4>
                            <span className="pdp-episode-duration">
                              {formatEpisodeDuration(episode)}
                            </span>
                          </div>
                          <p className="pdp-episode-description">
                            {episode.short_description ||
                              episode.description ||
                              'Episode description will be available when playing.'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400">No episodes available</p>
              )}
            </div>
          )}

          {/* Related Content Section */}
          {loading ? (
            <RelatedContentSkeleton />
          ) : currentRelatedContent.length > 0 ? (
            <div className="pdp-related-section">
              <h3 className="pdp-related-title">More Like This</h3>
              <div className="pdp-related-grid">
                {currentRelatedContent.slice(0, 12).map(relatedItem => (
                  <div
                    key={relatedItem._id}
                    className="pdp-related-card"
                    onClick={() => {
                      // Navigate to watch page using channel slug
                      if (relatedItem.slug) {
                        navigate(`/watch/${relatedItem.slug}`);
                        onClose();
                      } else {
                        console.warn(
                          'PDPModal: No slug available for related item:',
                          relatedItem.title,
                        );
                      }
                    }}
                  >
                    <div className="pdp-related-thumbnail">
                      <img
                        src={optimizeImageUrl(
                          relatedItem.spotlight_poster ||
                            relatedItem.image?.spotlight_poster ||
                            relatedItem.poster ||
                            relatedItem.thumb ||
                            relatedItem.image?.poster ||
                            relatedItem.wallpaper ||
                            relatedItem.image?.wallpaper ||
                            '/placeholder-image.jpg',
                          getResponsiveImagePreset('RELATED_CONTENT'),
                        )}
                        alt={relatedItem.title}
                        className="pdp-related-image"
                      />

                      {/* Related content overlay info */}
                      <div className="pdp-related-overlay">
                        <div className="pdp-related-overlay-top">
                          {relatedItem.duration &&
                            typeof relatedItem.duration === 'number' &&
                            relatedItem.duration > 0 && (
                              <span className="pdp-related-duration">
                                {formatDuration(relatedItem.duration)}
                              </span>
                            )}
                        </div>
                        <div className="pdp-related-overlay-bottom">
                          <div className="pdp-related-metadata">
                            {relatedItem.rating && (
                              <span className="pdp-related-rating">{relatedItem.rating}</span>
                            )}
                            {relatedItem.year && (
                              <span className="pdp-related-year">{relatedItem.year}</span>
                            )}
                          </div>
                          <button
                            className={`pdp-related-add-button ${
                              checkIsInWatchlist(relatedItem._id) ? 'in-watchlist' : ''
                            }`}
                            onClick={async e => {
                              e.stopPropagation();
                              await handleWatchlistToggleForChannel(relatedItem);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              {(() => {
                                // Use the service method which now handles parent-child relationships
                                const isInList = checkIsInWatchlist(relatedItem._id, null);

                                return isInList ? (
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                ) : (
                                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                                );
                              })()}
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Related content description */}
                    <div className="pdp-related-info">
                      <p className="pdp-related-description">
                        {(relatedItem.description && relatedItem.description.trim()) ||
                          (relatedItem.short_description && relatedItem.short_description.trim()) ||
                          'No description available.'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Quick Purchase Modal - for users with saved payment method */}
      {showQuickPurchaseModal && (
        <div className="pdp-quick-purchase-overlay" onClick={() => setShowQuickPurchaseModal(false)}>
          <div className="pdp-quick-purchase-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="pdp-quick-purchase-close" 
              onClick={() => setShowQuickPurchaseModal(false)}
              disabled={isPurchasing}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            
            <div className="pdp-quick-purchase-header">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
              <h3>Go Ad-Free</h3>
            </div>
            
            <div className="pdp-quick-purchase-content">
              <p className="pdp-quick-purchase-title">{title}</p>
              <p className="pdp-quick-purchase-description">
                Remove ads from this content for a one-time purchase.
              </p>
              
              {productData?.price && (
                <div className="pdp-quick-purchase-price">
                  <span className="price-label">Price:</span>
                  <span className="price-value">{productData.price}</span>
                </div>
              )}
              
              <div className="pdp-quick-purchase-payment-info">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                </svg>
                <span>Using your saved payment method</span>
              </div>
              
              {purchaseError && (
                <div className="pdp-quick-purchase-error">
                  {purchaseError}
                </div>
              )}
            </div>
            
            <div className="pdp-quick-purchase-actions">
              <button 
                className="pdp-quick-purchase-cancel" 
                onClick={() => setShowQuickPurchaseModal(false)}
                disabled={isPurchasing}
              >
                Cancel
              </button>
              <button 
                className="pdp-quick-purchase-confirm" 
                onClick={handleQuickPurchaseConfirm}
                disabled={isPurchasing}
              >
                {isPurchasing ? (
                  <>
                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 11-6.219-8.56" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>Confirm Purchase</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default PDPModal;
